import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ICurrentTemplate,
  IReport,
  ISelectedWizardDataSources,
  ITemplate
} from 'src/models/customReport';
import {
  IGranularity,
  TContentType,
  TPostOwnership,
  TRetweetFilter,
  TSentiment,
  TValidation,
  TTalkType,
  TAgeGeneration
} from 'src/models/general';
import { TSearchType } from 'src/models/onlineMedia';
import { TMetricCategory } from 'src/services/useCustomReport';

export type TSourceType =
  | 'social_media'
  | 'online_media'
  | 'print_media'
  | 'broadcast';

export type TDataType =
  | 'object'
  | 'group'
  | 'comparison'
  | 'clipping'
  | 'comparisonOnm'
  | 'otherMedia'
  | 'comparisonPrint'
  | 'comparisonBroadcast'
  | 'clippingOtherMedia'; // for print and broadcast

export type TVisualization =
  | 'bar_chart'
  | 'bar_chart_horizontal'
  | 'bubble'
  | 'line_chart'
  | 'table'
  | 'pie'
  | 'text'
  | 'positioning_map'
  | 'sample_post'
  | 'sample_talk'
  | 'sample_talk_single'
  | 'sample_post_single'
  | 'sample_article_multiple'
  | 'sample_article_single'
  | 'stack_bar_chart'
  | 'word_cloud';

export interface IContent {
  widgetConfigId: string;
  titleContent: string;
  sourceType: TSourceType;
  dataSource: TDataType;
  objectList: Array<string>;
  objectGroupId: string;
  comparisonId: string;
  metric: string;
  category: TMetricCategory;
  visualization: TVisualization;
  granularity?: IGranularity;
  quantity?: number;
  sortingByColumn?: string;
  page?: number; // index of sample
  // advance search --------------
  includeKeywords?: Array<string>;
  excludeKeywords?: Array<string>;
  fromUsername?: string;
  sentiment?: Array<TSentiment>;
  postOwnership?: Array<TPostOwnership>;
  contentTypeList?: Array<TContentType>;
  validationList?: Array<TValidation>;
  labelList?: Array<string>;
  replyToOriginalId?: Array<string>;
  showEmptyLabel?: boolean;
  sentimentConfidenceLevelMin?: number;
  sentimentConfidenceLevelMax?: number;
  timezone?: number;
  retweetFilter?: TRetweetFilter;
  talkType?: TTalkType[];
  ageGeneration?: TAgeGeneration[];
  // advance search online media
  clippingId: string;
  comparisonIdOnlineMedia: string;
  comparisonIdOfflineMedia: string;
  mediaList?: Array<string>;
  searchType?: TSearchType;
  languageList?: Array<string>;
  limitRow?: number;
}

export const advanceSearchInitialStateSM = {
  includeKeywords: [],
  excludeKeywords: [],
  sentiment: [],
  fromUsername: '',
  postOwnership: [],
  validationList: [],
  contentTypeList: [],
  labelList: [],
  replyToOriginalId: [],
  showEmptyLabel: false,
  sentimentConfidenceLevelMin: 0,
  sentimentConfidenceLevelMax: 1
  // timezone: 7
  // TODO: add talk Type and retweetFilter
};

export const advanceSearchInitialStateONM = {
  sentiment: [],
  validationList: [],
  mediaList: [],
  includeKeywords: [],
  excludeKeywords: [],
  searchType: 'allSearch',
  labelList: []
};

interface ILayoutContents {
  title: string;
  subTitle: string;
  contents: Array<IContent> | null;
}

export type TSlideType =
  | 'titleSlide'
  | 'singleContents'
  | 'twoContents'
  | 'threeContents'
  | 'fourContents'
  | 'nineContents'
  | 'sectionHeader'
  | 'oneTwoContents'
  | 'oneThreeContents'
  | 'twoOneContents'
  | 'threeOneContents'
  | 'verticalFourContents'
  | 'sixContents';

export interface ISlide {
  id: string;
  slideType: TSlideType;
  pagePosition: number;
  summary?: string;
  summaryPosition?: 'top' | 'left';
  details: {
    titleSlide: ILayoutContents | null;
    singleContents: ILayoutContents | null;
    twoContents: ILayoutContents | null;
    threeContents: ILayoutContents | null;
    fourContents: ILayoutContents | null;
    nineContents: ILayoutContents | null;
    sectionHeader: ILayoutContents | null;
    oneTwoContents: ILayoutContents | null;
    oneThreeContents: ILayoutContents | null;
    twoOneContents: ILayoutContents | null;
    threeOneContents: ILayoutContents | null;
    verticalFourContents: ILayoutContents | null;
    sixContents: ILayoutContents | null;
  };
}

export enum TActiveField {
  title = -1,
  content1 = 0,
  content2 = 1,
  content3,
  content4,
  content5,
  content6,
  content7,
  content8,
  content9
}

interface IIsSaved {
  [id: string]: boolean;
}

const initialIsSaved = {
  '-1': true, // for title
  '0': true, // for content1
  '1': true, // for content2
  '2': true, // for content3
  '3': true, // for content4
  '4': true, // for content5
  '5': true, // for content6
  '6': true, // for content7
  '7': true, // for content8
  '8': true // for content9
};

export interface ISavedDataSource {
  sourceType: TSourceType;
  dataSource: TDataType;
  objectList: Array<string>;
  objectGroupId: string;
  comparisonId: string;
  // advance search --------------
  includeKeywords?: Array<string>;
  excludeKeywords?: Array<string>;
  fromUsername?: string;
  sentiment?: Array<TSentiment>;
  postOwnership?: Array<TPostOwnership>;
  contentTypeList?: Array<TContentType>;
  validationList?: Array<TValidation>;
  labelList?: Array<string>;
  replyToOriginalId?: Array<string>;
  showEmptyLabel?: boolean;
  sentimentConfidenceLevelMin?: number;
  sentimentConfidenceLevelMax?: number;
  timezone?: number;
  retweetFilter?: TRetweetFilter;
  talkType?: TTalkType[];
  ageGeneration?: TAgeGeneration[];
  // advance search online media
  clippingId: string;
  comparisonIdOnlineMedia: string;
  mediaList?: Array<string>;
  searchType?: TSearchType;
  languageList?: Array<string>;
}

///////////////////////////////////////////////////////////
export interface ICustomReportSlice {
  reportList: {
    result: Array<IReport>;
    page: number;
    limit: number;
    totalRows: number;
    totalPages: number;
  };
  selectedReport: IReport | undefined;
  isSaved: IIsSaved;
  activeField: TActiveField;
  activeSlide: ISlide | undefined;
  slideList: Array<ISlide>;
  totalSlideRows: number;
  refetchSignal: boolean;
  showSummary: boolean;
  savedDataSource: ISavedDataSource;
  templateList: {
    result: Array<ITemplate>;
    page: number;
    limit: number;
    totalRows: number;
    totalPages: number;
  };
  currentTemplate: ICurrentTemplate;
  selectedWizardDataSource: Array<ISelectedWizardDataSources>;
}

const initialState: ICustomReportSlice = {
  reportList: {
    result: [],
    page: 1,
    limit: 10,
    totalRows: 0,
    totalPages: 0
  },
  selectedReport: undefined,
  isSaved: initialIsSaved,
  activeField: -1,
  activeSlide: undefined,
  slideList: [],
  totalSlideRows: 0,
  refetchSignal: false,
  showSummary: false,
  savedDataSource: {
    includeKeywords: null,
    excludeKeywords: null,
    fromUsername: null,
    sentiment: null,
    postOwnership: null,
    contentTypeList: null,
    validationList: null,
    labelList: null,
    sentimentConfidenceLevelMin: 0,
    sentimentConfidenceLevelMax: 1,
    showEmptyLabel: null,
    replyToOriginalId: [],
    retweetFilter: 'all',
    ageGeneration: [],
    mediaList: null,
    searchType: null,
    sourceType: null,
    dataSource: null,
    clippingId: null,
    comparisonId: null,
    comparisonIdOnlineMedia: null,
    objectGroupId: null,
    objectList: [],
    languageList: [],
    talkType: null,
    timezone: null
  },
  templateList: {
    result: [],
    page: 1,
    limit: 10,
    totalRows: 0,
    totalPages: 0
  },
  currentTemplate: null,
  selectedWizardDataSource: []
};

export const customReport = createSlice({
  name: 'customReport',
  initialState,
  reducers: {
    reducerSetReportList: (
      state: ICustomReportSlice,
      action: PayloadAction<{
        result: Array<IReport>;
        page: number;
        limit: number;
        totalRows: number;
        totalPages: number;
      }>
    ) => {
      state.reportList = action.payload;
    },
    reducerSetSelectedReport: (
      state: ICustomReportSlice,
      action: PayloadAction<IReport>
    ) => {
      state.selectedReport = action.payload;
    },
    reducerAddSlide: (
      state: ICustomReportSlice,
      action: PayloadAction<ISlide>
    ) => {
      state.slideList = [...state.slideList, action.payload];
    },
    reducerSetActiveSlide: (
      state: ICustomReportSlice,
      action: PayloadAction<ISlide>
    ) => {
      state.activeSlide = action.payload;
      state.activeField = -1;
      state.isSaved = initialIsSaved;
    },
    reducerSetActiveField: (
      state: ICustomReportSlice,
      action: PayloadAction<TActiveField>
    ) => {
      state.activeField = action.payload;
    },
    reducerSetActiveSlideTitle: (
      state: ICustomReportSlice,
      action: PayloadAction<string>
    ) => {
      state.activeSlide.details[state.activeSlide.slideType] = {
        ...state.activeSlide.details[state.activeSlide.slideType],
        title: action.payload
      };
      state.isSaved[-1] = false;
    },
    reducerSetActiveSlideSummary: (
      state: ICustomReportSlice,
      action: PayloadAction<{
        summary: string;
        summaryPosition: 'top' | 'left';
      }>
    ) => {
      state.activeSlide = {
        ...state.activeSlide,
        ...action.payload
      };
      state.isSaved[-1] = false;
    },
    reducerSetActiveSlideSubtitle: (
      state: ICustomReportSlice,
      action: PayloadAction<string>
    ) => {
      state.activeSlide.details[state.activeSlide.slideType] = {
        ...state.activeSlide.details[state.activeSlide.slideType],
        subTitle: action.payload
      };
      state.isSaved[-1] = false;
    },
    reducerUpdateActiveSlideContent: (
      state: ICustomReportSlice,
      action: PayloadAction<{
        data: Partial<IContent>;
      }>
    ) => {
      state.activeSlide.details[state.activeSlide.slideType].contents[
        state.activeField
      ] = {
        ...state.activeSlide.details[state.activeSlide.slideType].contents[
          state.activeField
        ],
        ...action.payload.data
      };
      state.isSaved[state.activeField] = false;
    },
    reducerSetSlides: (
      state: ICustomReportSlice,
      action: PayloadAction<ISlide[]>
    ) => {
      state.slideList = action.payload;
    },
    reducerSaveSlide: (
      state: ICustomReportSlice,
      action: PayloadAction<ISlide>
    ) => {
      const editedSlides = state.slideList.map((slide) => {
        if (slide.id === action.payload.id) return action.payload;
        return slide;
      });
      state.slideList = editedSlides;
      state.isSaved = initialIsSaved;
    },
    reducerHandleDiscard: (
      state: ICustomReportSlice,
      action: PayloadAction<{
        data: {
          title?: string;
          content?: IContent;
        };
      }>
    ) => {
      if (state.activeField === -1) {
        state.activeSlide.details[state.activeSlide.slideType].title =
          action.payload.data.title;
      } else {
        state.activeSlide.details[state.activeSlide.slideType].contents[
          state.activeField
        ] = action.payload.data.content;
      }
      state.isSaved[state.activeField] = true;
    },
    reducerRefetchSignal: (
      state: ICustomReportSlice,
      action: PayloadAction<boolean>
    ) => {
      state.refetchSignal = action.payload;
    },
    reducerSetShowSummary: (
      state: ICustomReportSlice,
      action: PayloadAction<boolean>
    ) => {
      state.showSummary = action.payload;
    },
    reducerSetTotalSlideRows: (
      state: ICustomReportSlice,
      action: PayloadAction<number>
    ) => {
      state.totalSlideRows = action.payload;
    },
    reducerUpdateSavedDataSource: (
      state: ICustomReportSlice,
      action: PayloadAction<{
        data: Partial<ISavedDataSource>;
      }>
    ) => {
      state.savedDataSource = {
        ...state.savedDataSource,
        ...action.payload.data
      };
    },
    reducerResetSavedDataSource: (state: ICustomReportSlice) => {
      state.savedDataSource = initialState.savedDataSource;
    },
    reducerSetTemplateList: (
      state: ICustomReportSlice,
      action: PayloadAction<{
        result: Array<ITemplate>;
        page: number;
        limit: number;
        totalRows: number;
        totalPages: number;
      }>
    ) => {
      state.templateList = action.payload;
    },
    reducerSetCurrentTemplate: (
      state: ICustomReportSlice,
      action: PayloadAction<ICurrentTemplate>
    ) => {
      state.currentTemplate = action.payload;
    },
    reducerSetNextSectionNumber: (state: ICustomReportSlice) => {
      state.currentTemplate.currentSectionPosition =
        state.currentTemplate.currentSectionPosition + 1;
    },
    reducerSetPreviousSectionNumber: (state: ICustomReportSlice) => {
      state.currentTemplate.currentSectionPosition =
        state.currentTemplate.currentSectionPosition - 1;
    },
    reducerSetInitialSelectedWizardDataSource: (
      state: ICustomReportSlice,
      action: PayloadAction<ISelectedWizardDataSources[]>
    ) => {
      state.selectedWizardDataSource = [
        ...state.selectedWizardDataSource,
        ...action.payload
      ];
    },
    reducerSetSelectedWizardDataSource: (
      state: ICustomReportSlice,
      action: PayloadAction<ISelectedWizardDataSources>
    ) => {
      state.selectedWizardDataSource = state.selectedWizardDataSource.map(
        (item) => {
          return {
            ...item,
            primaryId:
              item.sectionId === action.payload.sectionId &&
              item.dataSourceId === action.payload.dataSourceId
                ? action.payload.primaryId
                : item.primaryId
          };
        }
      );
    },
    reducerAutoFillDataSource: (
      state: ICustomReportSlice,
      action: PayloadAction<{
        dataSourceIdSource: string;
        dataSourceIdTarget: string;
      }>
    ) => {
      const { dataSourceIdSource, dataSourceIdTarget } = action.payload;
      const sourceData = state.selectedWizardDataSource.find(
        (item) => item.dataSourceId === dataSourceIdSource
      );
      if (sourceData) {
        state.selectedWizardDataSource = state.selectedWizardDataSource.map(
          (item) => {
            if (item.dataSourceId === dataSourceIdTarget) {
              return {
                ...item,
                primaryId: sourceData.primaryId
              };
            }
            return item;
          }
        );
      }
    },
    reducerResetWizard: (state: ICustomReportSlice) => {
      state.currentTemplate = initialState.currentTemplate;
      state.selectedWizardDataSource = initialState.selectedWizardDataSource;
    }
  }
});

export const {
  reducerSetReportList,
  reducerSetSelectedReport,
  reducerAddSlide,
  reducerSetActiveSlide,
  reducerSetActiveField,
  reducerSetActiveSlideTitle,
  reducerSetActiveSlideSummary,
  reducerSetActiveSlideSubtitle,
  reducerUpdateActiveSlideContent,
  reducerSetSlides,
  reducerSaveSlide,
  reducerHandleDiscard,
  reducerRefetchSignal,
  reducerSetShowSummary,
  reducerSetTotalSlideRows,
  reducerUpdateSavedDataSource,
  reducerResetSavedDataSource,
  reducerSetTemplateList,
  reducerSetCurrentTemplate,
  reducerSetNextSectionNumber,
  reducerSetPreviousSectionNumber,
  reducerSetInitialSelectedWizardDataSource,
  reducerSetSelectedWizardDataSource,
  reducerResetWizard,
  reducerAutoFillDataSource
} = customReport.actions;

export default customReport.reducer;
